/**
 * /publisher-caps — FS-822 operator route.
 *
 * Mounts the table-centric publisher cap view at `/publisher-caps`. A
 * deep-link search param `?affiliateId=<id>` prefills the toolbar so the
 * page can land from a publisher detail screen with caps already loaded.
 *
 * Root wires this route into `router.tsx` after prod merge — agents never
 * touch router.tsx directly (dash contract rule D7).
 */

import { Link, createRoute } from "@tanstack/react-router";
import { rootRoute } from "./__root";
import { PublisherCapsPage } from "@/features/publisher-caps/PublisherCapsPage";

interface PublisherCapsSearch {
	affiliateId?: string;
}

export const publisherCapsRoute = createRoute({
	getParentRoute: () => rootRoute,
	path: "/publisher-caps",
	validateSearch: (raw: Record<string, unknown>): PublisherCapsSearch => {
		const affiliateId = raw.affiliateId;
		if (typeof affiliateId === "string" && affiliateId.trim().length > 0) {
			return { affiliateId: affiliateId.trim() };
		}
		return {};
	},
	component: PublisherCapsRouteComponent,
});

export default publisherCapsRoute;

export function PublisherCapsRouteComponent() {
	const { affiliateId } = publisherCapsRoute.useSearch();

	return (
		<main className="mx-auto w-full max-w-7xl px-6 py-6">
			<nav aria-label="Breadcrumb" className="mb-4 text-sm text-zinc-500">
				<ol className="flex flex-wrap items-center gap-1">
					<li>
						<Link
							to="/"
							className="rounded-md px-1 py-0.5 hover:bg-zinc-100 hover:text-zinc-900"
						>
							Dashboard
						</Link>
					</li>
					<li aria-hidden="true" className="text-zinc-300">
						/
					</li>
					<li>
						<span className="rounded-md px-1 py-0.5 text-zinc-600">
							Publishers
						</span>
					</li>
					<li aria-hidden="true" className="text-zinc-300">
						/
					</li>
					<li className="font-medium text-zinc-900">Caps</li>
				</ol>
			</nav>

			<PublisherCapsPage initialAffiliateId={affiliateId} />
		</main>
	);
}
